import { FC, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Card,
    Chip,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Collapse,
    Checkbox,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { Calendar } from "react-multi-date-picker";
import moment from "moment";
import "moment/locale/fr";
import frenchLocale from "data/locale";
import { useSelector } from "react-redux";
import { RootState } from "store";
import StayType from "models/StayType";
import Duration from "models/Duration";
import Budget from "models/Budget";
import Transport from "models/Transport";
import SearchCriteria from "models/TransferModels/SearchCriteria";
import classes from "./style";
import { useNavigate, useLocation } from "react-router-dom";
import { toggleByKey } from "utils/filters";
import ClickAwayListener from "@mui/base/ClickAwayListener";

moment.locale("fr");

interface Props { searchCriteria?: SearchCriteria }

interface State {
    openedCalendar: boolean;
    openedStayTypes: boolean;
    openedDurations: boolean;
    openedBudgets: boolean;
    openedTransports: boolean;
    selectedDate: Date[];
    selectedStayTypes: StayType[];
    selectedDurations: Duration[];
    selectedBudgets: Budget[];
    selectedTransports: Transport[];
    destinationSearch: string;
}

const initialState: State = {
    openedCalendar: false,
    openedStayTypes: false,
    openedDurations: false,
    openedBudgets: false,
    openedTransports: false,
    selectedDate: [],
    selectedStayTypes: [],
    selectedDurations: [],
    selectedBudgets: [],
    selectedTransports: [],
    destinationSearch: "",
};

const normalizeToDateArray = (input: any): Date[] => {
    if (!input) return [];
    const arr = Array.isArray(input) ? input : [input];
    return arr
        .map((v) =>
            v && typeof v === "object" && typeof (v as any).toDate === "function"
                ? (v as any).toDate()
                : new Date(v),
        )
        .filter((d) => d instanceof Date && !Number.isNaN(d.getTime()));
};

const RechercheVoyage: FC<Props> = ({ searchCriteria }) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const isAuthenticated = useSelector((s: RootState) => s.globalSlice.isAuthenticated);
    const { stayTypes, durations, budgets, transports } = useSelector((s: RootState) => s.globalSlice);

    const [state, setState] = useState<State>(initialState);
    const weekDays = useMemo(() => ["D", "L", "M", "W", "J", "V", "S"], []);

    useEffect(() => {
        if (!searchCriteria) return;
        setState((prev) => ({
            ...prev,
            selectedDate: normalizeToDateArray([searchCriteria.from as any, searchCriteria.to as any]),
            selectedStayTypes: searchCriteria.stayTypes ?? [],
            selectedDurations: searchCriteria.durations ?? [],
            selectedBudgets: searchCriteria.budgets ?? [],
            selectedTransports: searchCriteria.transports ?? [],
        }));
    }, [searchCriteria]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") resetPanels();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    useEffect(() => {
        setState((p) => ({
            ...p,
            openedCalendar: false,
            openedStayTypes: false,
            openedDurations: false,
            openedBudgets: false,
            openedTransports: false,
        }));
    }, [isMobile]);

    const resetPanels = (overrides?: Partial<State>) =>
        setState((p) => ({
            ...p,
            openedCalendar: false,
            openedStayTypes: false,
            openedDurations: false,
            openedBudgets: false,
            openedTransports: false,
            ...(overrides ?? {}),
        }));

    const canSearch = state.selectedDate.length === 2;

    const handleSubmit = () => {
        if (!canSearch) return;

        navigate("/liste-pays", {
            state: {
                searchCriteria: {
                    from: new Date(state.selectedDate[0]),
                    to: new Date(state.selectedDate[1]),
                    stayTypes: state.selectedStayTypes,
                    durations: state.selectedDurations,
                    budgets: state.selectedBudgets,
                    transports: state.selectedTransports,
                    destinations: [],
                } as SearchCriteria,
            },
        });
    };

    const DesktopOpeners = (
        <Grid container sx={[classes.rechercheVoyage, pathname !== "/je-cherche-un-voyage" && classes.bgGray]}>
            <Grid item xs={12} lg onClick={() => resetPanels({ openedCalendar: !state.openedCalendar })}>
                <Button
                    aria-expanded={state.openedCalendar}
                    aria-controls="panel-calendar"
                    sx={classes.fieldBtn}
                    onClick={(e) => {
                        e.stopPropagation();
                        resetPanels({ openedCalendar: !state.openedCalendar });
                    }}
                >
                    Quand :
                </Button>
                {state.selectedDate.length === 2 && (
                    <Typography variant="body1">
                        {`${moment(new Date(state.selectedDate[0])).format("ll")} > ${moment(
                            new Date(state.selectedDate[1]),
                        ).format("ll")}`}
                    </Typography>
                )}
            </Grid>
            <Grid item xs={12} lg onClick={() => resetPanels({ openedStayTypes: !state.openedStayTypes })}>
                <Button
                    aria-expanded={state.openedStayTypes}
                    aria-controls="panel-staytypes"
                    sx={classes.fieldBtn}
                    onClick={(e) => {
                        e.stopPropagation();
                        resetPanels({ openedStayTypes: !state.openedStayTypes });
                    }}
                >
                    Ambiance :
                </Button>
                <Box>
                    {state.selectedStayTypes.slice(0, 4).map((s) => (
                        <Chip key={s.id} label={s.name} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                </Box>
            </Grid>
            <Grid item xs={12} lg onClick={() => resetPanels({ openedDurations: !state.openedDurations })}>
                <Button
                    aria-expanded={state.openedDurations}
                    aria-controls="panel-durations"
                    sx={classes.fieldBtn}
                    onClick={(e) => {
                        e.stopPropagation();
                        resetPanels({ openedDurations: !state.openedDurations });
                    }}
                >
                    Temps de transport :
                </Button>
                <Box>
                    {state.selectedDurations.slice(0, 4).map((d) => (
                        <Chip key={d.id} label={d.name} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                </Box>
            </Grid>
            <Grid item xs={12} lg onClick={() => resetPanels({ openedBudgets: !state.openedBudgets })}>
                <Button
                    aria-expanded={state.openedBudgets}
                    aria-controls="panel-budgets"
                    sx={classes.fieldBtn}
                    onClick={(e) => {
                        e.stopPropagation();
                        resetPanels({ openedBudgets: !state.openedBudgets });
                    }}
                >
                    Budget :
                </Button>
                <Box>
                    {state.selectedBudgets.slice(0, 4).map((b) => (
                        <Chip key={b.id} label={b.description} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                </Box>
            </Grid>
            <Grid item xs={12} lg onClick={() => resetPanels({ openedTransports: !state.openedTransports })}>
                <Button
                    aria-expanded={state.openedTransports}
                    aria-controls="panel-transports"
                    sx={classes.fieldBtn}
                    onClick={(e) => {
                        e.stopPropagation();
                        resetPanels({ openedTransports: !state.openedTransports });
                    }}
                >
                    Transport :
                </Button>
                <Box>
                    {state.selectedTransports.slice(0, 4).map((t) => (
                        <Chip key={t.id} label={t.name} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                </Box>
            </Grid>
            <Grid item xs={12} lg={1}>
                <Button sx={classes.fieldBtn} variant="contained" disabled={!canSearch} onClick={handleSubmit}>
                    <SearchOutlinedIcon />
                </Button>
            </Grid>
        </Grid>
    );

    const DesktopPanels = (
        <Grid container sx={[classes.rechercheVoyageFields]}>
            <Grid item xs={12} lg id="panel-calendar">
                {state.openedCalendar && (
                    <Calendar
                        locale={frenchLocale}
                        minDate={new Date()}
                        className={state.selectedDate.length > 1 ? "range" : "single"}
                        range
                        weekDays={weekDays}
                        weekStartDayIndex={1}
                        value={state.selectedDate as any}
                        onChange={(value: any) => setState((p) => ({ ...p, selectedDate: normalizeToDateArray(value) }))}
                    />
                )}
            </Grid>

            <Grid item xs={12} lg id="panel-staytypes">
                {state.openedStayTypes && (
                    <List dense>
                        {stayTypes.map((ambiance) => (
                            <ListItem key={ambiance.id} disablePadding>
                                <ListItemButton
                                    role={undefined}
                                    onClick={() =>
                                        setState((p) => ({
                                            ...p,
                                            selectedStayTypes: toggleByKey(ambiance, p.selectedStayTypes, "id"),
                                        }))
                                    }
                                    dense
                                >
                                    <ListItemText primary={ambiance.name} secondary={ambiance.description} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Grid>

            <Grid item xs={12} lg id="panel-durations">
                {state.openedDurations && (
                    <List dense>
                        {durations.map((duration) => (
                            <ListItem key={duration.id} disablePadding>
                                <ListItemButton
                                    role={undefined}
                                    onClick={() =>
                                        setState((p) => ({
                                            ...p,
                                            selectedDurations: toggleByKey(duration, p.selectedDurations, "id"),
                                        }))
                                    }
                                    dense
                                >
                                    <ListItemText primary={duration.name} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Grid>

            <Grid item xs={12} lg id="panel-budgets">
                {state.openedBudgets && (
                    <List dense>
                        {budgets.map((budget) => (
                            <ListItem key={budget.id} disablePadding>
                                <ListItemButton
                                    role={undefined}
                                    onClick={() =>
                                        setState((p) => ({
                                            ...p,
                                            selectedBudgets: toggleByKey(budget, p.selectedBudgets, "id"),
                                        }))
                                    }
                                    dense
                                >
                                    <ListItemText primary={budget.description} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Grid>

            <Grid item xs={12} lg id="panel-transports">
                {state.openedTransports && (
                    <List dense>
                        {transports.map((transport) => (
                            <ListItem key={transport.id} disablePadding>
                                <ListItemButton
                                    role={undefined}
                                    onClick={() =>
                                        setState((p) => ({
                                            ...p,
                                            selectedTransports: toggleByKey(transport, p.selectedTransports, "id"),
                                        }))
                                    }
                                    dense
                                >
                                    <ListItemText primary={transport.name} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Grid>
        </Grid>
    );

    const MobileLayout = (
        <Grid container sx={{ p: 2 }}>
            <Grid item xs={12}>
                <Button
                    sx={classes.fieldBtn}
                    onClick={() =>
                        setState((s) => ({
                            ...s,
                            openedCalendar: !s.openedCalendar,
                            openedStayTypes: false,
                            openedDurations: false,
                            openedBudgets: false,
                            openedTransports: false,
                        }))
                    }
                >
                    Quand :
                    {state.selectedDate.length === 2 && (
                        <Typography variant="body1" sx={{ ml: 1 }}>
                            {`${moment(new Date(state.selectedDate[0])).format("ll")} > ${moment(
                                new Date(state.selectedDate[1]),
                            ).format("ll")}`}
                        </Typography>
                    )}
                </Button>
                <Collapse in={state.openedCalendar}>
                    <Calendar
                        locale={frenchLocale}
                        minDate={new Date()}
                        className={state.selectedDate.length > 1 ? "range" : "single"}
                        range
                        weekDays={weekDays}
                        weekStartDayIndex={1}
                        value={state.selectedDate as any}
                        onChange={(value: any) =>
                            setState((p) => ({ ...p, selectedDate: normalizeToDateArray(value) }))
                        }
                    />
                </Collapse>
            </Grid>

            <Grid item xs={12}>
                <Button
                    sx={classes.fieldBtn}
                    onClick={() =>
                        setState((s) => ({
                            ...s,
                            openedCalendar: false,
                            openedStayTypes: !s.openedStayTypes,
                            openedDurations: false,
                            openedBudgets: false,
                            openedTransports: false,
                        }))
                    }
                >
                    Ambiance :
                    {state.selectedStayTypes.length > 0 && (
                        <Typography variant="body1" sx={{ ml: 1 }}>
                            {state.selectedStayTypes.map((e) => e.name).join(", ")}
                        </Typography>
                    )}
                </Button>
                <Collapse in={state.openedStayTypes}>
                    <List dense>
                        {stayTypes.map((ambiance) => {
                            const checked = !!state.selectedStayTypes.find((e) => e.id === ambiance.id);
                            return (
                                <ListItem key={ambiance.id} disablePadding>
                                    <ListItemButton
                                        dense
                                        onClick={() =>
                                            setState((p) => ({
                                                ...p,
                                                selectedStayTypes: toggleByKey(ambiance, p.selectedStayTypes, "id"),
                                            }))
                                        }
                                    >
                                        <ListItemIcon>
                                            <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                                        </ListItemIcon>
                                        <ListItemText primary={ambiance.name} secondary={ambiance.description} />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </Collapse>
            </Grid>

            <Grid item xs={12}>
                <Button
                    sx={classes.fieldBtn}
                    onClick={() =>
                        setState((s) => ({
                            ...s,
                            openedCalendar: false,
                            openedStayTypes: false,
                            openedDurations: !s.openedDurations,
                            openedBudgets: false,
                            openedTransports: false,
                        }))
                    }
                >
                    Temps de transport :
                    {state.selectedDurations.length > 0 && (
                        <Typography variant="body1" sx={{ ml: 1 }}>
                            {state.selectedDurations.map((e) => e.name).join(", ")}
                        </Typography>
                    )}
                </Button>
                <Collapse in={state.openedDurations}>
                    <List dense>
                        {durations.map((duration) => {
                            const checked = !!state.selectedDurations.find((e) => e.id === duration.id);
                            return (
                                <ListItem key={duration.id} disablePadding>
                                    <ListItemButton
                                        dense
                                        onClick={() =>
                                            setState((p) => ({
                                                ...p,
                                                selectedDurations: toggleByKey(duration, p.selectedDurations, "id"),
                                            }))
                                        }
                                    >
                                        <ListItemIcon>
                                            <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                                        </ListItemIcon>
                                        <ListItemText primary={duration.name} />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </Collapse>
            </Grid>

            <Grid item xs={12}>
                <Button
                    sx={classes.fieldBtn}
                    onClick={() =>
                        setState((s) => ({
                            ...s,
                            openedCalendar: false,
                            openedStayTypes: false,
                            openedDurations: false,
                            openedBudgets: !s.openedBudgets,
                            openedTransports: false,
                        }))
                    }
                >
                    Budget :
                    {state.selectedBudgets.length > 0 && (
                        <Typography variant="body1" sx={{ ml: 1 }}>
                            {state.selectedBudgets.map((e) => e.description).join(", ")}
                        </Typography>
                    )}
                </Button>
                <Collapse in={state.openedBudgets}>
                    <List dense>
                        {budgets.map((budget) => {
                            const checked = !!state.selectedBudgets.find((e) => e.id === budget.id);
                            return (
                                <ListItem key={budget.id} disablePadding>
                                    <ListItemButton
                                        dense
                                        onClick={() =>
                                            setState((p) => ({
                                                ...p,
                                                selectedBudgets: toggleByKey(budget, p.selectedBudgets, "id"),
                                            }))
                                        }
                                    >
                                        <ListItemIcon>
                                            <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                                        </ListItemIcon>
                                        <ListItemText primary={budget.description} />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </Collapse>
            </Grid>

            <Grid item xs={12}>
                <Button
                    sx={classes.fieldBtn}
                    onClick={() =>
                        setState((s) => ({
                            ...s,
                            openedCalendar: false,
                            openedStayTypes: false,
                            openedDurations: false,
                            openedBudgets: false,
                            openedTransports: !s.openedTransports,
                        }))
                    }
                >
                    Transport :
                    {state.selectedTransports.length > 0 && (
                        <Typography variant="body1" sx={{ ml: 1 }}>
                            {state.selectedTransports.map((e) => e.name).join(", ")}
                        </Typography>
                    )}
                </Button>
                <Collapse in={state.openedTransports}>
                    <List dense>
                        {transports.map((transport) => {
                            const checked = !!state.selectedTransports.find((e) => e.id === transport.id);
                            return (
                                <ListItem key={transport.id} disablePadding>
                                    <ListItemButton
                                        dense
                                        onClick={() =>
                                            setState((p) => ({
                                                ...p,
                                                selectedTransports: toggleByKey(transport, p.selectedTransports, "id"),
                                            }))
                                        }
                                    >
                                        <ListItemIcon>
                                            <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                                        </ListItemIcon>
                                        <ListItemText primary={transport.name} />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </Collapse>
            </Grid>

            <Grid item xs={12}>
                <Button fullWidth variant="contained" disabled={!canSearch} onClick={handleSubmit}>
                    <SearchOutlinedIcon />
                </Button>
            </Grid>
        </Grid>
    );

    return (
        <Card sx={[classes.card, classes.fullWidth, isMobile ? {} : classes.inlineFields]}>
            <ClickAwayListener
                onClickAway={() => resetPanels()}
                mouseEvent="onMouseDown"
                touchEvent="onTouchStart"
            >
                {isMobile ? (
                    MobileLayout
                ) : (
                    <Box>
                        {DesktopOpeners}
                        {DesktopPanels}
                    </Box>
                )}
            </ClickAwayListener>
        </Card>
    );
};

export default RechercheVoyage;
